import React, { useState, useEffect, useMemo } from 'react';
import { Users, CheckCircle2, X } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import { getCctvRollingOrders } from '../../data/socialProofData';

// Realistic time offsets
const TIME_STEPS = [
  { ur: '3 منٹ پہلے', en: '3 mins ago' },
  { ur: '7 منٹ پہلے', en: '7 mins ago' },
  { ur: '14 منٹ پہلے', en: '14 mins ago' },
  { ur: '22 منٹ پہلے', en: '22 mins ago' },
  { ur: '31 منٹ پہلے', en: '31 mins ago' },
  { ur: '42 منٹ پہلے', en: '42 mins ago' },
  { ur: '53 منٹ پہلے', en: '53 mins ago' },
  { ur: '1 گھنٹہ پہلے', en: '1 hour ago' },
  { ur: '2 گھنٹے پہلے', en: '2 hours ago' },
  { ur: '3 گھنٹے پہلے', en: '3 hours ago' },
  { ur: '4 گھنٹے پہلے', en: '4 hours ago' }
];

// Product quantity helper tailored dynamically to each product's actual variants
const getProductQuantities = (prod) => {
  if (!prod) return [{ ur: '1 پیکٹ', en: '1 Pack' }];

  // 1. If explicit product variants exist, strictly and dynamically use their defined weights!
  if (Array.isArray(prod.variants) && prod.variants.length > 0) {
    return prod.variants.map((v, i) => {
      const wUr = v.weight || `${v.grams || ''} گرام`;
      const wEn = v.weightEn || `${v.grams || ''}g`;
      if (i === 0) return { ur: `${wUr}`, en: `${wEn}` };
      if (i === 1) return { ur: `1 پیکٹ (${wUr})`, en: `1 Pack (${wEn})` };
      if (i === 2) return { ur: `2 ڈبے (${wUr})`, en: `2 Boxes (${wEn})` };
      return { ur: `${wUr}`, en: `${wEn}` };
    });
  }

  // 2. Fallbacks for products without custom variants
  return [
    { ur: '1 پیکٹ', en: '1 Pack' },
    { ur: '2 ڈبے', en: '2 Boxes' },
    { ur: '1 ڈبہ', en: '1 Box' },
    { ur: '2 عدد', en: '2 Units' }
  ];
};

const getDailyVisitorCount = () => {
  try {
    const todayStr = new Date().toISOString().split('T')[0];
    const saved = localStorage.getItem('himalayan_daily_visitors');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.date === todayStr && typeof parsed.count === 'number') {
        const increment = Math.random() > 0.4 ? 1 : 0;
        const newCount = parsed.count + increment;
        localStorage.setItem('himalayan_daily_visitors', JSON.stringify({ date: todayStr, count: newCount }));
        return newCount;
      }
    }
    const currentHour = new Date().getHours();
    const baseVisitors = 520 + Math.floor(Math.random() * 50) + Math.floor(currentHour * 18);
    localStorage.setItem('himalayan_daily_visitors', JSON.stringify({ date: todayStr, count: baseVisitors }));
    return baseVisitors;
  } catch {
    return 714;
  }
};

const SocialProofToast = ({ isSuppressed = false }) => {
  const { lang } = useLanguage();
  const { orders = [], products = [], settings = {} } = useStore();
  const { setSelectedProductModal, selectedProductModal } = useCart();

  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visitorCount, setVisitorCount] = useState(getDailyVisitorCount);

  // Filter only currently existing, valid products
  const validProducts = useMemo(() => {
    return (products || []).filter(p => Boolean(p && (p.nameUrdu || p.nameEn)));
  }, [products]);

  // Stable product-specific visitor count
  const productVisitorCount = useMemo(() => {
    if (!selectedProductModal) return 42;
    const seed = (selectedProductModal.id || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return 32 + (seed % 43);
  }, [selectedProductModal?.id]);

  // Periodic visitor count increment
  useEffect(() => {
    const interval = setInterval(() => {
      setVisitorCount(prev => prev + (Math.random() > 0.5 ? 1 : 0));
    }, 40000);
    return () => clearInterval(interval);
  }, []);

  // When selectedProductModal opens or changes, reset index and show product-specific toast promptly
  useEffect(() => {
    if (selectedProductModal) {
      setCurrentIndex(0);
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2400);
      return () => clearTimeout(timer);
    }
  }, [selectedProductModal?.id]);

  // Build dynamic notifications pool strictly depending on active valid products in the store
  const notifications = useMemo(() => {
    if (validProducts.length === 0) {
      return [{ type: 'site_visitors', count: visitorCount }];
    }

    const pool = [];

    // =========================================================================
    // CASE A: Visitor is on a SPECIFIC PRODUCT page/modal
    // =========================================================================
    if (selectedProductModal) {
      const activeProd = validProducts.find(p => p.id === selectedProductModal.id) || selectedProductModal;
      const quantities = getProductQuantities(activeProd);

      // 1. Real orders for this specific product
      (orders || []).forEach((order, idx) => {
        const item = order.items?.find(it => 
          it.id === activeProd.id || 
          it.name === activeProd.nameUrdu || 
          it.name === activeProd.nameEn
        );

        if (item) {
          const qtyText = item.quantity 
            ? (lang === 'ur' ? `${item.quantity} عدد` : `${item.quantity} units`) 
            : (lang === 'ur' ? '1 عدد' : '1 unit');

          pool.push({
            type: 'product_purchase',
            customerName: order.customerName || (lang === 'ur' ? 'محترم کسٹمر' : 'Customer'),
            city: order.city || (lang === 'ur' ? 'لاہور' : 'Lahore'),
            quantityUrdu: item.variant ? `${item.variant}` : qtyText,
            quantityEn: item.variant ? `${item.variant}` : qtyText,
            productNameUrdu: activeProd.nameUrdu,
            productNameEn: activeProd.nameEn || activeProd.nameUrdu,
            product: activeProd,
            image: activeProd.image || activeProd.images?.[0] || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80',
            timeAgoUrdu: idx === 0 ? 'چند لمحے پہلے' : `${(idx + 1) * 16} منٹ پہلے`,
            timeAgoEn: idx === 0 ? 'Just now' : `${(idx + 1) * 16} mins ago`,
            isReal: true
          });
        }
      });

      // 2. 7-Day Rolling Weekly CCTV order stream for THIS specific product
      const rollingOrders = getCctvRollingOrders(activeProd, lang);

      rollingOrders.forEach((order, i) => {
        const qtyObj = quantities[i % quantities.length];

        pool.push({
          type: 'product_purchase',
          customerName: order.name,
          city: order.city,
          quantityUrdu: qtyObj.ur,
          quantityEn: qtyObj.en,
          productNameUrdu: activeProd.nameUrdu,
          productNameEn: activeProd.nameEn || activeProd.nameUrdu,
          product: activeProd,
          image: activeProd.image || activeProd.images?.[0] || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80',
          timeAgoUrdu: order.timeAgoUrdu,
          timeAgoEn: order.timeAgoEn,
          isReal: false
        });
      });

      // 3. Interleave product-specific visitor badge
      const finalPool = [];
      pool.forEach((item, idx) => {
        finalPool.push(item);
        if ((idx + 1) % 3 === 0) {
          finalPool.push({
            type: 'product_visitors',
            count: productVisitorCount,
            productName: lang === 'ur' ? activeProd.nameUrdu : (activeProd.nameEn || activeProd.nameUrdu)
          });
        }
      });

      return finalPool;
    }

    // =========================================================================
    // CASE B: Visitor is on the HOME PAGE / General browsing
    // =========================================================================
    // 1. Real orders from across the store (strictly for currently existing products)
    (orders || []).forEach((order, idx) => {
      const existingItem = order.items?.find(it => 
        validProducts.some(p => p.id === it.id || p.nameUrdu === it.name || p.nameEn === it.name)
      );

      if (existingItem) {
        const matchedProd = validProducts.find(p => 
          p.id === existingItem.id || 
          p.nameUrdu === existingItem.name || 
          p.nameEn === existingItem.name
        );

        if (matchedProd) {
          pool.push({
            type: 'home_purchase',
            customerName: order.customerName || (lang === 'ur' ? 'محترم کسٹمر' : 'Valued Customer'),
            city: order.city || (lang === 'ur' ? 'لاہور' : 'Lahore'),
            quantityUrdu: existingItem.variant || '',
            quantityEn: existingItem.variant || '',
            productNameUrdu: matchedProd.nameUrdu,
            productNameEn: matchedProd.nameEn || matchedProd.nameUrdu,
            product: matchedProd,
            image: matchedProd.image || matchedProd.images?.[0] || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80',
            timeAgoUrdu: idx === 0 ? 'چند منٹ پہلے' : `${(idx + 1) * 18} منٹ پہلے`,
            timeAgoEn: idx === 0 ? 'A few mins ago' : `${(idx + 1) * 18} mins ago`,
            isReal: true
          });
        }
      }
    });

    // 2. 7-Day Rolling Weekly order stream dynamically rotating across all active store products
    const homeRollingOrders = getCctvRollingOrders(null, lang);

    homeRollingOrders.forEach((order, idx) => {
      const prod = validProducts[idx % validProducts.length];
      const quantities = getProductQuantities(prod);
      const qtyObj = quantities[idx % quantities.length];

      pool.push({
        type: 'home_purchase',
        customerName: order.name,
        city: order.city,
        quantityUrdu: qtyObj?.ur || '',
        quantityEn: qtyObj?.en || '',
        productNameUrdu: prod.nameUrdu,
        productNameEn: prod.nameEn || prod.nameUrdu,
        product: prod,
        image: prod.image || prod.images?.[0] || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80',
        timeAgoUrdu: order.timeAgoUrdu,
        timeAgoEn: order.timeAgoEn,
        isReal: false
      });
    });

    // 3. Interleave site-wide visitor counter
    const finalPool = [];
    pool.forEach((item, idx) => {
      finalPool.push(item);
      if ((idx + 1) % 3 === 0) {
        finalPool.push({
          type: 'site_visitors',
          count: visitorCount
        });
      }
    });

    if (finalPool.length > 1 && finalPool[1]?.type !== 'site_visitors') {
      finalPool.splice(1, 0, { type: 'site_visitors', count: visitorCount });
    } else if (finalPool.length === 0) {
      finalPool.push({ type: 'site_visitors', count: visitorCount });
    }

    return finalPool;
  }, [selectedProductModal, orders, validProducts, visitorCount, productVisitorCount, lang]);

  const isEnabled = (settings?.enableSocialProof ?? true) && !isSuppressed && !isDismissed;

  // Toast display & auto-cycling timer
  useEffect(() => {
    if (!isEnabled || notifications.length === 0) {
      setIsVisible(false);
      return;
    }

    let hideTimer;
    const showTimer = setTimeout(() => {
      setIsVisible(true);

      hideTimer = setTimeout(() => {
        if (!isHovered) {
          setIsVisible(false);
        }
      }, 5500);

    }, 3200);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [isDismissed, isSuppressed, notifications.length]);

  // When visibility turns false, wait 7.5 seconds and advance to next notification
  useEffect(() => {
    if (!isVisible && !isDismissed && !isSuppressed && notifications.length > 0) {
      const cycleTimer = setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % notifications.length);
        setIsVisible(true);
      }, 7500);

      return () => clearTimeout(cycleTimer);
    }
  }, [isVisible, isDismissed, isSuppressed, notifications.length]);

  // Auto-hide when visible
  useEffect(() => {
    if (isVisible && !isHovered) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 5800);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isHovered]);

  const currentItem = notifications[currentIndex] || notifications[0];
  if (!currentItem || !isEnabled) return null;

  const handleClick = () => {
    if (currentItem.product && !selectedProductModal) {
      setSelectedProductModal(currentItem.product);
      setIsVisible(false);
    }
  };

  const isRtl = lang === 'ur';

  const fontStyle = {
    fontFamily: '"Noto Sans Arabic", "Segoe UI", Tahoma, system-ui, sans-serif'
  };

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={fontStyle}
      className={`social-proof-toast fixed bottom-4 sm:bottom-6 left-3 sm:left-6 z-[60] w-auto max-w-[340px] sm:max-w-[420px] transition-all duration-500 ease-out select-none ${
        isVisible 
          ? 'opacity-100 translate-y-0 pointer-events-auto scale-100' 
          : 'opacity-0 translate-y-8 pointer-events-none scale-95'
      }`}
    >
      <div 
        onClick={handleClick}
        className={`relative bg-white/95 backdrop-blur-md rounded-2xl sm:rounded-3xl p-2.5 sm:p-3 border border-emerald-100 shadow-xl flex items-center gap-3 transition-all duration-300 ${
          currentItem.product && !selectedProductModal ? 'cursor-pointer hover:border-emerald-300 hover:shadow-2xl' : 'cursor-default'
        }`}
      >
        {/* Close Button: Positioned on the OPPOSITE side of the image so it NEVER overlaps! */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsVisible(false);
            setIsDismissed(true);
          }}
          className={`absolute top-2 w-5 h-5 rounded-full bg-white text-gray-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition shadow-xs border border-gray-200 cursor-pointer z-10 ${
            isRtl ? 'left-2' : 'right-2'
          }`}
          title={isRtl ? "بند کریں" : "Close"}
        >
          <X className="w-3 h-3" />
        </button>

        {/* Product Image or Visitor Icon */}
        {currentItem.type === 'site_visitors' || currentItem.type === 'product_visitors' ? (
          <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 flex items-center justify-center shrink-0 shadow-xs">
            <Users className="w-5 h-5 text-emerald-700" />
          </div>
        ) : (
          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-50 border border-emerald-100 shrink-0 shadow-xs flex items-center justify-center">
            <img
              src={currentItem.image}
              alt={currentItem.productNameUrdu || ''}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80';
              }}
            />
          </div>
        )}

        {/* Text Details with Slim, Crisp Typography */}
        <div className={`flex-1 min-w-0 ${isRtl ? 'pl-5 pr-1 text-right' : 'pr-5 pl-1 text-left'}`}>
          {/* Site Visitors */}
          {currentItem.type === 'site_visitors' && (
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-sm sm:text-base text-gray-900 tracking-tight font-subheading">
                  {visitorCount.toLocaleString()} {isRtl ? 'افراد' : 'People'}
                </span>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <p className="text-xs text-gray-600 font-subheading leading-tight mt-0.5">
                {isRtl ? 'آج اس ویب سائٹ پر تشریف لا چکے ہیں' : 'Have visited this website today'}
              </p>
            </div>
          )}

          {/* Product Specific Visitors */}
          {currentItem.type === 'product_visitors' && (
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-sm sm:text-base text-emerald-900 tracking-tight font-subheading">
                  {currentItem.count} {isRtl ? 'افراد' : 'Visitors'}
                </span>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <p className="text-xs text-gray-600 font-subheading leading-tight mt-0.5">
                {isRtl ? 'نے آج یہ پراڈکٹ دیکھی یا خریدی' : 'viewed or ordered this item today'}
              </p>
            </div>
          )}

          {/* Product Specific Purchase (Shown when viewing a product modal) */}
          {currentItem.type === 'product_purchase' && (
            <div>
              {/* Row 1: Customer Name & City Badge */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-xs sm:text-[13px] text-gray-900 font-subheading truncate max-w-[130px] sm:max-w-[170px]">
                  {currentItem.customerName}
                </span>
                <span className="text-[10px] sm:text-[11px] text-emerald-800 font-subheading bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full font-medium shrink-0">
                  {isRtl ? `${currentItem.city} سے` : `from ${currentItem.city}`}
                </span>
              </div>

              {/* Row 2: Quantity ordered */}
              <div className="text-xs sm:text-[13px] text-gray-800 font-subheading mt-1 leading-snug">
                {isRtl ? (
                  <>نے <span className="font-bold text-emerald-900">{currentItem.quantityUrdu}</span> کا آرڈر دیا</>
                ) : (
                  <>Ordered <span className="font-bold text-emerald-900">{currentItem.quantityEn}</span></>
                )}
              </div>

              {/* Row 3: Time and Verified Badge */}
              <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-400 mt-1">
                <span className="font-subheading text-gray-500">
                  {isRtl ? currentItem.timeAgoUrdu : currentItem.timeAgoEn}
                </span>
                <span>•</span>
                <span className="text-emerald-700 font-medium font-subheading flex items-center gap-0.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600 inline shrink-0" />
                  <span>{isRtl ? 'تصدیق شدہ آرڈر' : 'Verified Order'}</span>
                </span>
              </div>
            </div>
          )}

          {/* Home Page Purchase (Shown on home page across all products) */}
          {currentItem.type === 'home_purchase' && (
            <div>
              {/* Row 1: Customer Name & City Badge */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-xs sm:text-[13px] text-gray-900 font-subheading truncate max-w-[130px] sm:max-w-[170px]">
                  {currentItem.customerName}
                </span>
                <span className="text-[10px] sm:text-[11px] text-emerald-800 font-subheading bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full font-medium shrink-0">
                  {isRtl ? `${currentItem.city} سے` : `from ${currentItem.city}`}
                </span>
              </div>

              {/* Row 2: Product Name & Variant */}
              <div className="text-xs sm:text-[13px] text-gray-800 font-subheading mt-1 leading-snug truncate">
                {isRtl ? (
                  <>
                    نے <strong className="font-bold text-emerald-900">{currentItem.productNameUrdu}</strong>
                    {currentItem.quantityUrdu ? <span className="text-emerald-800 font-medium"> ({currentItem.quantityUrdu})</span> : ''}
                    {' '}کا آرڈر دیا
                  </>
                ) : (
                  <>
                    Ordered <strong className="font-bold text-emerald-900">{currentItem.productNameEn}</strong>
                    {currentItem.quantityEn ? <span className="text-emerald-800 font-medium"> ({currentItem.quantityEn})</span> : ''}
                  </>
                )}
              </div>

              {/* Row 3: Time and Verified Badge */}
              <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-400 mt-1">
                <span className="font-subheading text-gray-500">
                  {isRtl ? currentItem.timeAgoUrdu : currentItem.timeAgoEn}
                </span>
                <span>•</span>
                <span className="text-emerald-700 font-medium font-subheading flex items-center gap-0.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600 inline shrink-0" />
                  <span>{isRtl ? 'تصدیق شدہ آرڈر' : 'Verified Order'}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialProofToast;
